/**
 * AlgorithmBrowser — Browse 50-algorithm registry, filter by category/compute/problem type
 * Phase 4.8-B — WQ-198
 * Phase 4.8-C — WQ-201: compute tier badges + environment pill + compatible-only filter
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon, ChevronDownIcon, ChevronRightIcon } from '../common/Icons';
import { listAlgorithms, getCategoriesSummary, type Algorithm, type CategorySummary } from '../../services/algorithmService';
import {
  getComputeCapability,
  type ComputeCapability,
} from '../../services/computeCapabilityService';
import { ComputeTierBadge } from './ComputeTierBadge';
import AlgorithmRegistrationWizard from './AlgorithmRegistrationWizard';
import BiPanelErrorBoundary from './BiPanelErrorBoundary';

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e', inactive: '#6b7280', deprecated: '#f97316',
  experimental: '#a855f7', candidate: '#eab308',
};

const AlgorithmBrowser: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [algorithms, setAlgorithms] = useState<Algorithm[]>([]);
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // WQ-201
  const [capability, setCapability] = useState<ComputeCapability | null>(null);
  const [compatibleOnly, setCompatibleOnly] = useState(false);
  // WQ-242
  const [wizardOpen, setWizardOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, string> = {};
      if (categoryFilter) filters.category = categoryFilter;
      if (statusFilter) filters.status = statusFilter;
      const [algos, cats, cap] = await Promise.all([
        listAlgorithms(authFetch, filters),
        getCategoriesSummary(authFetch).catch(() => [] as CategorySummary[]),
        getComputeCapability(authFetch).catch(() => null),
      ]);
      setAlgorithms(Array.isArray(algos) ? algos : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setCapability(cap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load algorithms');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, categoryFilter, statusFilter]);

  const filtered = algorithms.filter(a => {
    if (
      searchQuery &&
      !a.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !a.category?.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !a.problem_type?.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    if (compatibleOnly) {
      const compat = a.current_compatibility;
      if (compat && compat.compatible === false) return false;
    }
    return true;
  });

  const envPillLabel = (() => {
    if (!capability) return null;
    if (capability.gpu_available) {
      const model = capability.gpu_model || 'GPU';
      const vram = capability.gpu_vram_mb_per_device
        ? ` · ${Math.round(capability.gpu_vram_mb_per_device / 1024)}GB VRAM`
        : '';
      return `Running on: ${capability.gpu_count}× ${model}${vram}`;
    }
    const amx = capability.intel_amx_available ? ' · AMX enabled' : '';
    return `Running on: CPU-only · ${capability.cpu_count} vCPU${amx}`;
  })();

  const uniqueCategories = [...new Set(algorithms.map(a => a.category).filter(Boolean))].sort();

  if (loading && algorithms.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading algorithms...</div>;
  }

  return (
    <div>
      {/* WQ-201: current compute environment pill */}
      {envPillLabel && (
        <div
          data-testid="compute-env-pill"
          style={{
            display: 'inline-block',
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 12,
            background: capability?.gpu_available ? '#ecfdf5' : '#f1f5f9',
            color: capability?.gpu_available ? '#047857' : '#475569',
            border: '1px solid var(--border)',
            marginBottom: 12,
            fontWeight: 500,
          }}
        >
          {envPillLabel}
        </div>
      )}
      {/* Category summary bar */}
      {categories.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {categories.slice(0, 8).map(cat => (
            <button
              key={cat.category}
              onClick={() => setCategoryFilter(cat.category === categoryFilter ? '' : cat.category)}
              style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 12,
                border: '1px solid var(--border)', cursor: 'pointer',
                background: cat.category === categoryFilter ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                color: cat.category === categoryFilter ? '#fff' : 'var(--text-primary)',
                fontWeight: 500,
              }}
            >
              {cat.category} ({cat.count})
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <SearchIcon size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text" placeholder="Search algorithms..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <select
          value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Categories</option>
          {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deprecated">Deprecated</option>
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-primary)' }}>
          <input
            type="checkbox"
            data-testid="compatible-only-toggle"
            checked={compatibleOnly}
            onChange={e => setCompatibleOnly(e.target.checked)}
          />
          Compatible with current environment only
        </label>
        <button
          data-testid="alg-wizard-open-button"
          onClick={() => setWizardOpen(true)}
          style={{
            fontSize: 13, padding: '6px 12px', borderRadius: 6,
            border: '1px solid var(--accent-primary)', fontWeight: 600,
            cursor: 'pointer', background: 'var(--accent-primary)', color: '#fff',
          }}
        >
          + Register Algorithm
        </button>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
          fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {searchQuery || categoryFilter ? 'No algorithms match your filters.' : 'No algorithms registered.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', width: 30 }}></th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Category</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Problem Type</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Compute</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(algo => (
                <React.Fragment key={algo.id}>
                  <tr
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === algo.id ? null : algo.id)}
                  >
                    <td style={{ padding: '8px 12px' }}>
                      {expandedId === algo.id ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{algo.name}</td>
                    <td style={{ padding: '8px 12px' }}>{algo.category || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{algo.problem_type || '—'}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12 }}>
                      {algo.compute_tier
                        ? <ComputeTierBadge tier={algo.compute_tier} />
                        : (algo.compute_profile || '—')}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: `${STATUS_COLORS[algo.status] || '#888'}20`,
                        color: STATUS_COLORS[algo.status] || '#888',
                      }}>{algo.status}</span>
                    </td>
                  </tr>
                  {expandedId === algo.id && (
                    <tr style={{ background: 'var(--bg-secondary)' }}>
                      <td colSpan={6} style={{ padding: '12px 24px' }}>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
                          {algo.description || 'No description available.'}
                        </div>
                        {algo.hyperparameter_schema && (
                          <div style={{ marginBottom: 8 }}>
                            <strong style={{ fontSize: 12 }}>Hyperparameters:</strong>
                            <pre style={{ fontSize: 11, background: 'var(--bg-primary)', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                              {JSON.stringify(algo.hyperparameter_schema, null, 2)}
                            </pre>
                          </div>
                        )}
                        {algo.data_requirements && (
                          <div>
                            <strong style={{ fontSize: 12 }}>Data Requirements:</strong>
                            <pre style={{ fontSize: 11, background: 'var(--bg-primary)', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                              {JSON.stringify(algo.data_requirements, null, 2)}
                            </pre>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
        Showing {filtered.length} of {algorithms.length} algorithms
      </div>
      <BiPanelErrorBoundary panelName="AlgorithmRegistrationWizard">
        <AlgorithmRegistrationWizard
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onSubmitted={() => {
            setWizardOpen(false);
            fetchData();
          }}
        />
      </BiPanelErrorBoundary>
    </div>
  );
};

export default AlgorithmBrowser;
